/**
 * Service for handling Wealthica callback side effects
 * 
 * This service handles store mutations and other side effects that should
 * occur after successful callback processing, maintaining separation of
 * concerns from pure callback parameter extraction.
 */

import { logger } from '../utils/logger';
import { extractWealthica2CallbackParams } from '../utils/callback';
import { WealthicaConnectionService } from './WealthicaConnectionService';
import { WealthicaAuthService } from './WealthicaAuthService';
import type { WealthicaCallbackParams, WealthicaCallbackResult, WealthicaConnection } from '../types';
import { isDemoProvider } from '../utils/identifiers';

export class WealthicaCallbackService {
  /**
   * Process Wealthica callback parameters and handle connection workflow
   * 
   * @param params - Optional parameters to override URL parameters
   * @returns Result of callback processing
   */
  static async processCallback(params?: WealthicaCallbackParams, userId?: string): Promise<WealthicaCallbackResult> {
    try {
      logger.info('WealthicaCallbackService.processCallback', 'Processing Wealthica callback', {
        userId: userId?.substring(0, 8) + '...',
        hasParams: !!params,
        currentUrl: typeof window !== 'undefined' ? window.location.href : 'undefined'
      });

      // Parse callback parameters
      const { code, state, provider, error: callbackError, access_token } = extractWealthica2CallbackParams(params);
      
      // ENHANCED: Log extracted parameters for debugging
      logger.info('WealthicaCallbackService.processCallback', 'Extracted callback parameters', {
        hasCode: !!code,
        codeLength: code?.length || 0,
        hasState: !!state,
        provider: provider || 'undefined',
        hasAccessToken: !!access_token,
        hasCallbackError: !!callbackError,
        callbackError: callbackError || 'none'
      });
      
      // Handle callback errors
      if (callbackError) {
        logger.error('WealthicaCallbackService.processCallback', 'Error from callback', { error: callbackError });
        await this.handleFailedConnection(callbackError);
        return { success: false, error: callbackError };
      }

      // Check for required parameters
      if (!code) {
        logger.error('WealthicaCallbackService.processCallback', 'Missing code parameter in callback', {
          extractedParams: { code, state, provider, access_token },
          currentUrl: typeof window !== 'undefined' ? window.location.href : 'undefined'
        });
        return { success: false, error: 'Missing authorization code' };
      }

      // Extract stable institution identifier for consistent mapping
      const rawInstitutionId = code; // Original Wealthica institution ID
      let stableInstitutionId: string;
      
      // Use shared demo detection helper
      const isDemoInstitution = isDemoProvider(provider, undefined, rawInstitutionId);

      // Apply stable identifier mapping logic for consistent connections
      if (isDemoInstitution) {
        // Demo institution with rotating ID - use stable identifier
        stableInstitutionId = 'demo';
        logger.info('WealthicaCallbackService.processCallback', 'Demo institution detected', {
          rawId: rawInstitutionId.substring(0, 8) + '...',
          stableId: stableInstitutionId,
          detectionReason: 'identifiers:isDemoProvider'
        });
      } else {
        // Regular institution - use provider name as stable identifier (e.g. 'wealthsimple')
        stableInstitutionId = provider || 'wealthica';
        logger.info('WealthicaCallbackService.processCallback', 'Real institution detected', {
          rawId: rawInstitutionId.substring(0, 8) + '...',
          stableId: stableInstitutionId,
          provider: provider
        });
      }

      // Construct connection data from callback parameters
      const connectionData = {
        institutionId: rawInstitutionId, // OAuth rotating ID for database storage
        institutionIdentifier: stableInstitutionId, // Stable financial institution identifier (e.g. 'wealthsimple')
        providerName: stableInstitutionId === 'demo' ? 'demo' : 'wealthica', // Internal provider type
        loginName: stableInstitutionId === 'demo' ? 'demo' : 'user', // Provide proper login name
        timestamp: Date.now(), // Use number timestamp as required by the type
        state: state || '',
        originalProvider: provider || 'wealthica2',
        // Add other connection properties as needed
        connectionId: `conn_${stableInstitutionId}_${Date.now()}`,
        lastSyncAt: new Date().toISOString()
      };

      // Get bearer token from callback or via Edge Function (server-only tokens)
      let bearerToken = access_token || '';
      if (!bearerToken) {
        logger.info('WealthicaCallbackService.processCallback', 'No access_token in callback, requesting server-side token');
        const tokenResult = userId
          ? await WealthicaAuthService.getToken(userId)
          : { success: false, error: 'Missing userId for token retrieval' } as const;

        if (!tokenResult.success || !tokenResult.data) {
          const errMsg = tokenResult.error || 'Could not obtain authentication token. Please try reconnecting.';
          logger.error('WealthicaCallbackService.processCallback', 'Server-side token retrieval failed', {
            error: errMsg
          });
          return {
            success: false,
            error: errMsg
          };
        }

        bearerToken = tokenResult.data;
        logger.info('WealthicaCallbackService.processCallback', 'Token retrieved from Edge Function');
      }

      // ENHANCED: Log before Edge Function call
      logger.info('WealthicaCallbackService.processCallback', 'About to call Edge Function for connection save', {
        userId: userId?.substring(0, 8) + '...',
        institutionId: code?.substring(0, 8) + '...',
        connectionDataKeys: Object.keys(connectionData),
        timestamp: new Date().toISOString()
      });

      // Handle successful connection - pass both raw and stable IDs
      await this.handleSuccessfulConnection(rawInstitutionId, connectionData, userId);
      
      // Return success with constructed connection data
      logger.info('WealthicaCallbackService.processCallback', 'Callback processed successfully', {
        hasBearerToken: !!bearerToken,
        tokenLength: bearerToken?.length || 0,
        timestamp: new Date().toISOString()
      });
      return { 
        success: true,
        connectionData,
        bearerToken,
        originalProvider: provider || 'wealthica2'
      };
      
    } catch (error) {
      logger.error('WealthicaCallbackService.processCallback', 'Failed to process callback', { error });
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * Handle successful connection callback
   * Sets persistence preference and performs other success-related side effects
   * 
   * @param rawInstitutionId - The raw institution ID from Wealthica callback (for edge function mapping)
   * @param connectionData - The connection data (contains stable institution ID)
   * @param userId - The user ID for connection saving
   */
  static async handleSuccessfulConnection(rawInstitutionId: string, connectionData: WealthicaConnection, userId?: string): Promise<void> {
    logger.info('WealthicaCallbackService', 'Handling successful connection', { 
      hasRawId: !!rawInstitutionId,
      rawIdLength: rawInstitutionId?.length || 0,
      stableId: connectionData.institutionId
    });
    
    try {
      // Save connection to database if userId is provided
      if (userId) {
        await WealthicaConnectionService.saveConnectionStatus({
          userId,
          institutionId: rawInstitutionId, // OAuth rotating ID
          institutionIdentifier: connectionData.institutionIdentifier, // Stable financial institution identifier
          providerName: connectionData.providerName,
          loginName: connectionData.loginName || (isDemoProvider(connectionData.providerName, connectionData.institutionIdentifier, connectionData.institutionId) ? 'demo' : 'unknown'),
          connectionStatus: 'connected'
        });
        logger.info('WealthicaCallbackService', 'Successfully saved connection to database');
      }
      
      // Do not toggle persistence here to avoid duplicate and racy persistence attempts.
      // The multi-store refresh/persist pipeline will handle persistence after connection setup.
    } catch (error) {
      logger.error('WealthicaCallbackService', 'Failed to handle successful connection', {
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }
  
  /**
   * Handle failed connection callback
   * Performs error-related side effects
   * 
   * @param error - The error from the callback
   * @param errorDescription - Optional error description
   */
  static async handleFailedConnection(error: string, errorDescription?: string): Promise<void> {
    logger.warn('WealthicaCallbackService', 'Handling failed connection', { error, errorDescription });
    
    try {
      // Future: Could handle error-specific side effects here
      // For now, just log the failure
      logger.info('WealthicaCallbackService', 'Failed connection handled');
    } catch (handlingError) {
      logger.error('WealthicaCallbackService', 'Failed to handle connection error', {
        originalError: error,
        handlingError: handlingError instanceof Error ? handlingError.message : String(handlingError)
      });
    }
  }
}
